import { useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { selectUser } from '../../../redux/selectors';
import { coreService } from '../../../services/core/core-service';
import { FrameworkReportsContext } from '../contexts/framework-reportings.context';
import dayjs from 'dayjs';
import { sortDates } from '../../../utils/dateUtils';

export const useFrameworkReporting = () => {
  const user = useSelector(selectUser);

  const { store } = useContext(FrameworkReportsContext);
  const {
    dateOption,
    filterAccounts,
    filterCsms,
    filterFrameworks,
    filterTypes,
    filterFromDate,
    filterToDate,
    frameworkReportExecutions,
    isLoading,
    page,
    paginationMetadata,
    setFilterFrameworks,
    setFilterTypes,
    setDateOption,
    setFilterAccounts,
    setFilterCsms,
    setFilterFromDate,
    setFilterToDate,
    setPage,
    refetch,
  } = store();

  const { data: frameworks, isFetching: isFetchingFrameworks } = useQuery({
    queryKey: ['frameworks', user?.id],
    queryFn: async () => {
      if (!user?.membership.organization.id) return [];
      return coreService.fetchFrameworksByWorkspaceId(user?.membership.organization.id);
    },
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });

  const handleDateOptionChanged = (value: string) => {
    let from;
    let to;

    if (value === '30d') {
      from = dayjs().subtract(30, 'day').toDate();
      to = new Date();
    }

    if (value === '60d') {
      from = dayjs().subtract(60, 'day').toDate();
      to = new Date();
    }

    if (value === '90d') {
      from = dayjs().subtract(90, 'day').toDate();
      to = new Date();
    }

    if (value === '120d') {
      from = dayjs().subtract(120, 'day').toDate();
      to = new Date();
    }

    if (from && to) {
      setFilterFromDate(from);
      setFilterToDate(to);
      setDateOption(value);
    }
  };

  const handleCalendarChange = (values: [Date, Date] | [null, null]) => {
    if (values[0] !== null && values[1] !== null) {
      const [firstDate, secondDate] = sortDates(values);
      setFilterFromDate(firstDate);
      setFilterToDate(secondDate);
    }
  };

  const resetFilters = () => {
    setFilterFrameworks([]);
    setFilterAccounts([]);
    setFilterFromDate(null);
    setFilterToDate(null);
    setDateOption('120d');
    refetch();
  };

  return {
    frameworks,
    isLoading,
    isFetchingFrameworks,
    page,
    filterFrameworks,
    filterTypes,
    filterAccounts,
    filterCsms,
    filterFromDate,
    filterToDate,
    dateOption,
    frameworkReportExecutions,
    paginationMetadata,
    setPage,
    setFilterFrameworks,
    setFilterTypes,
    setFilterAccounts,
    setFilterCsms,
    setFilterFromDate,
    setFilterToDate,
    setDateOption,
    handleDateOptionChanged,
    handleCalendarChange,
    resetFilters,
    refetch,
  };
};
